// es marketing copy. Mirrors the EN source 1:1 (see types.ts).
import type { MkPack } from './types'

const pack: MkPack = {
  "features": {
    "custom-domains": {
      "name": "Dominios propios",
      "title": "Firma electrónica en el dominio de cada cliente",
      "desc": "Firma documentos online en tu propio dominio, como sign.tuempresa.es. Añade un registro CNAME y verificamos el DNS y emitimos el certificado TLS solos.",
      "h1": "Tu dominio, tu marca, nuestro motor de firma",
      "lead": "Los destinatarios abren los enlaces de firma en sign.tuempresa.es, no en el nuestro. Cada organización tiene su propio dominio verificado con certificado TLS automático, sobre la misma plataforma compartida.",
      "sections": [
        {
          "h2": "Configurado en minutos",
          "body": "Añade el dominio en los ajustes de tu organización y crea un registro CNAME en tu proveedor de DNS. En cuanto el registro resuelve, sign.yt lo verifica y emite un certificado de Let's Encrypt con la primera solicitud.\n\nSin despliegues separados ni archivos de certificado que subir o renovar."
        },
        {
          "h2": "Cada solicitud llega a la organización correcta",
          "body": "La plataforma identifica la organización a partir de la cabecera Host de cada solicitud. Las páginas de firma, los correos y la pantalla de inicio de sesión en tu dominio muestran tu logotipo y tus colores, y los datos nunca pasan de un cliente a otro."
        },
        {
          "h2": "Pensado para operadores con muchas marcas",
          "body": "Las oficinas virtuales, los coworkings y las asesorías suelen gestionar varias marcas. Cada organización puede usar su propio dominio, su imagen de marca y su remitente de correo, mientras tú lo gestionas todo desde una sola cuenta."
        }
      ],
      "faq": [
        {
          "q": "¿Qué planes incluyen dominio propio?",
          "a": "Los dominios propios están disponibles a partir del plan Professional."
        },
        {
          "q": "¿Tengo que gestionar los certificados TLS?",
          "a": "No. Los certificados se emiten y renuevan automáticamente en cuanto tu registro DNS apunta a la plataforma."
        },
        {
          "q": "¿Puedo seguir usando la dirección de sign.yt?",
          "a": "Sí. Los enlaces en el dominio de la plataforma siguen funcionando; el dominio propio es un añadido, no una migración."
        },
        {
          "q": "¿Los enlaces de verificación también usan mi dominio?",
          "a": "El código QR y el enlace corto de cada documento firmado apuntan por defecto a verify.sign.yt. Llevarlos a tu propio dominio forma parte del complemento de pago Personalización completa."
        }
      ]
    },
    "white-label-branding": {
      "name": "Marca blanca",
      "title": "Firma electrónica de marca blanca",
      "desc": "Tu logotipo, tus colores y uno de cuatro temas en cada página de firma, de verificación y en cada correo. Cada equipo puede tener su imagen o puedes fijarla.",
      "h1": "Páginas de firma con la imagen de tu empresa",
      "lead": "Los destinatarios ven tu logotipo, tus colores y tu nombre desde el primer correo hasta el PDF firmado. sign.yt se queda en segundo plano.",
      "sections": [
        {
          "h2": "Tu marca en cada punto de contacto",
          "body": "Sube un logotipo, define un nombre de marca y un color principal, y los destinatarios los verán en la página de firma y en la página pública de verificación de cada documento firmado. Los mismos ajustes se aplican a los correos de notificación, junto con tu nombre de remitente y el pie de correo. La página de inicio de sesión admite además un favicon, un color secundario y un breve texto de bienvenida.\n\nElige uno de cuatro temas de página: clásico, minimalista, oscuro o llamativo. Cada tema funciona con tu color de marca, ajustado cuando hace falta para que el texto mantenga un contraste legible.\n\nLos destinatarios reconocen quién les pide la firma, lo que genera confianza y reduce las respuestas de \"¿esto es phishing?\" que tu equipo tiene que atender."
        },
        {
          "h2": "Una imagen distinta para cada equipo",
          "body": "¿Gestionas varias sedes o marcas dentro de una organización? Cada equipo puede sustituir el logotipo, los colores, el tema y el nombre de remitente. Un documento enviado por un equipo muestra su imagen en la página de firma, en la de verificación y en sus correos, así que un contrato de tu oficina de Varsovia se ve distinto de uno enviado desde Cracovia.\n\nCuando necesitas coherencia, un administrador de la organización puede bloquear la marca de los equipos con un solo interruptor, y todos los equipos vuelven a la imagen de la organización en todas partes."
        },
        {
          "h2": "Sin distintivo de la plataforma en los planes de pago",
          "body": "En el plan Starter, las páginas de inicio de sesión y de la organización muestran una pequeña nota \"Powered by sign.yt\". A partir del plan Professional esa nota desaparece y la experiencia es totalmente tuya. Combínalo con un dominio propio y los destinatarios firmarán en tu dirección, mientras sign.yt se queda en segundo plano.\n\nCada documento firmado lleva un código QR y un enlace corto como verify.sign.yt/k3Fq9ZxT2mPa7LwB a su página pública de verificación. Con el complemento de pago Personalización completa, estos enlaces también usan tu propio dominio.\n\nLa marca se resuelve en un único lugar, del equipo a la organización y a los valores por defecto de la plataforma, así que correos, páginas de firma y páginas de verificación se mantienen coherentes sin que nadie copie ajustes a mano."
        }
      ],
      "faq": [
        {
          "q": "¿Qué puedo personalizar?",
          "a": "Logotipo, nombre de marca, color principal, tema de página, nombre de remitente y pie de correo. La página de inicio de sesión admite además un favicon, un color secundario y un texto de bienvenida."
        },
        {
          "q": "¿Qué temas de página puedo elegir?",
          "a": "Cuatro: clásico, minimalista, oscuro y llamativo. El tema se aplica a la página de firma y a la página pública de verificación."
        },
        {
          "q": "¿Cada equipo puede tener su propia marca?",
          "a": "Sí. Un equipo puede sustituir el logotipo, los colores, el tema y el nombre de remitente. Sus documentos muestran esa imagen en la página de firma, en la de verificación y en los correos, salvo que un administrador bloquee la marca de los equipos para toda la organización."
        },
        {
          "q": "¿La marca se aplica también a los correos?",
          "a": "Sí. Los correos de notificación usan la marca de la organización, o la del equipo si la tiene, incluidos el nombre de remitente y el pie."
        },
        {
          "q": "¿Dónde aparece \"Powered by sign.yt\"?",
          "a": "Solo en el plan Starter, en las páginas de inicio de sesión y de la organización. Professional y los planes superiores lo eliminan."
        },
        {
          "q": "¿A dónde apuntan los enlaces de verificación?",
          "a": "Por defecto a verify.sign.yt, una dirección corta que abre la página de verificación del documento con tu marca. Los enlaces de verificación en tu propio dominio forman parte del complemento de pago Personalización completa."
        }
      ]
    },
    "pades-electronic-signature": {
      "name": "Firmas PAdES",
      "title": "Firma electrónica PAdES en cada PDF",
      "desc": "Cada firmante recibe una firma digital PAdES en el PDF, más un sello final de la plataforma y un certificado de finalización. Todo cambio posterior se detecta.",
      "h1": "Un sello criptográfico, no solo la imagen de una firma",
      "lead": "sign.yt hace más que pegar una firma dibujada en una página. Cada firma se incrusta en el PDF como firma PAdES, así que cualquier edición posterior del archivo es detectable.",
      "sections": [
        {
          "h2": "Una firma PAdES por firmante",
          "body": "Cuando un destinatario termina, sign.yt aplica una firma PAdES incremental con un certificado emitido para ese firmante por la autoridad de certificación de la plataforma. Las firmas se añaden una tras otra, así que las anteriores siguen siendo válidas.\n\nLas claves nuevas usan ECDSA P-384 con SHA-384. Cuando todas las partes han firmado, un sello final de la plataforma cierra el documento.\n\nCada firmante tiene su propia entrada en el panel de firmas del PDF, con su nombre y la hora de firma, en lugar de un sello anónimo para todo el documento."
        },
        {
          "h2": "Hora fiable y validación a largo plazo",
          "body": "sign.yt puede añadir a cada firma un sello de tiempo RFC 3161 de una autoridad de sellado de tiempo. Con el sellado configurado, el sello final se amplía con datos de validación y un sello de tiempo del documento (PAdES-B-LTA), para que la firma pueda comprobarse dentro de muchos años.\n\nSin autoridad de sellado de tiempo, las firmas se generan en el nivel básico PAdES-B-B. En ambos casos, la firma cubre cada byte del documento tal como estaba en ese momento."
        },
        {
          "h2": "Verificable fuera de sign.yt",
          "body": "El archivo firmado es un PDF estándar. Adobe Acrobat Reader y otros lectores de PDF compatibles con firmas muestran las firmas y señalan cualquier modificación hecha después de firmar.\n\nCualquiera puede además arrastrar el PDF al verificador público de sign.yt, sin cuenta, para ver quién firmó, cuándo y si los bytes están intactos.\n\nEl verificador comprueba si la firma cubre el archivo completo y si el documento lleva el sello de la plataforma sign.yt. Donde el validador DSS de la UE está activado, muestra también un veredicto eIDAS para cada firma.\n\nCada documento completado recibe un código QR y una dirección corta de verify.sign.yt en su certificado de finalización. Abre una página pública con los nombres de los firmantes enmascarados, las fechas de firma, el hash final, el sello y el estado del anclaje en Bitcoin, y permite a cualquiera comprobar que su copia del archivo es idéntica."
        },
        {
          "h2": "Evidencias adjuntas al documento",
          "body": "Al PDF firmado se añade un certificado de finalización. Para cada firmante recoge el correo, el método de verificación, las horas de envío, visualización y firma, la dirección IP y el agente de usuario, además de la declaración de consentimiento que marcó antes de firmar. Las evidencias viajan con el archivo, no solo en nuestra base de datos.\n\nEl hash del PDF final también se ancla en Bitcoin mediante OpenTimestamps, y todo el paquete puede descargarse como un único ZIP de evidencias.\n\nSi alguna vez se cuestiona una firma, tienes todo en un archivo que puedes entregar a un abogado, a un auditor o a un tribunal."
        }
      ],
      "faq": [
        {
          "q": "¿Una firma de sign.yt es legalmente vinculante?",
          "a": "Según eIDAS, no se pueden negar efectos jurídicos a una firma electrónica por el mero hecho de ser electrónica. Cuando la ley exige forma escrita, usa una firma electrónica cualificada de un prestador cualificado de servicios de confianza. Las firmas con Profil Zaufany (perfil oficial polaco de identidad digital) y e-dowód (DNI electrónico polaco) a través de Węzeł Podpisu (servicio estatal polaco de firma) también están disponibles donde se hayan activado."
        },
        {
          "q": "¿Adobe Reader mostrará las firmas?",
          "a": "Sí. Las firmas son PAdES estándar y aparecen en el panel de firmas. Que Adobe marque la identidad del firmante como de confianza depende de su propia lista de confianza."
        },
        {
          "q": "¿Qué pasa si alguien edita el PDF después de firmar?",
          "a": "La edición queda fuera del rango de bytes firmado, así que los lectores de PDF y el verificador de sign.yt indican que el documento ha sido modificado."
        },
        {
          "q": "¿Necesito mi propio certificado?",
          "a": "No. sign.yt emite automáticamente un certificado por firmante. Las firmas cualificadas están disponibles como método de firma aparte."
        }
      ]
    },
    "trusted-timestamps": {
      "name": "Sellos de tiempo",
      "title": "Sellos de tiempo RFC 3161 con TSA de respaldo",
      "desc": "Cada firma recibe un sello de tiempo RFC 3161 de una autoridad independiente. Primero Certum, y si no responde, DigiCert, Sectigo y FreeTSA como respaldo.",
      "h1": "Hora de firma confirmada por una autoridad independiente",
      "lead": "La hora de una firma de sign.yt no depende solo del reloj de nuestro servidor. También la firma una autoridad de sellado de tiempo, así que la fecha se sostiene cuando alguien la cuestiona.",
      "sections": [
        {
          "h2": "Cuatro autoridades, una firma",
          "body": "sign.yt pide primero el sello de tiempo a Certum. Si no responde, toma el relevo la siguiente autoridad de la lista: DigiCert, después Sectigo y después FreeTSA. Una firma siempre usa una sola autoridad de principio a fin, y la principal vuelve a usarse unos minutos después de recuperarse.\n\nSi ninguna autoridad está disponible, el documento se firma igualmente en el nivel PAdES-B-B en lugar de esperar indefinidamente."
        },
        {
          "h2": "Validación a largo plazo incluida",
          "body": "Con el sello de tiempo en su sitio, el sello final se amplía con datos de validación y un sello de tiempo del documento (PAdES-B-LTA). Las raíces de las cuatro autoridades vienen incluidas en la plataforma, así que la ruta de validación se construye sin configuración adicional.\n\nEso significa que la firma puede comprobarse dentro de muchos años, cuando los certificados de firma ya hayan caducado."
        },
        {
          "h2": "Cualificación visible en la página de verificación",
          "body": "Cuando subes un PDF firmado al verificador, cada sello de tiempo se contrasta con las listas de confianza de la UE. La página indica si es un sello de firma o de archivo, y si procede de una autoridad cualificada de sellado de tiempo (QTSA), cuya fecha goza de presunción legal de exactitud.",
          "requires": "eidas_validation"
        }
      ],
      "faq": [
        {
          "q": "¿Qué es un sello de tiempo RFC 3161?",
          "a": "Un pequeño token firmado por una autoridad de sellado de tiempo que demuestra que un hash concreto existía en un momento concreto. Se incrusta en la firma del PDF."
        },
        {
          "q": "¿Qué pasa si una autoridad de sellado de tiempo no responde?",
          "a": "sign.yt pasa a la siguiente de la lista. Solo si fallan todas se crea la firma sin sello de tiempo."
        },
        {
          "q": "¿Puedo usar mi propia autoridad de sellado de tiempo?",
          "a": "La lista de autoridades se define para toda la plataforma. Contáctanos si tus contratos exigen un prestador cualificado concreto."
        }
      ]
    },
    "bitcoin-anchoring": {
      "name": "Anclaje en Bitcoin",
      "title": "Hashes de documentos anclados en Bitcoin",
      "desc": "Cada documento completado se ancla en Bitcoin con OpenTimestamps. Cualquiera puede comprobar la prueba con herramientas de código abierto, sin fiarse de sign.yt.",
      "h1": "Una prueba que no depende de nosotros",
      "lead": "Cuando se completa un documento, su huella digital entra en un bloque de Bitcoin. Años después, cualquiera puede demostrar que el archivo existía en ese momento, aunque sign.yt ya no exista.",
      "sections": [
        {
          "h2": "Qué se ancla",
          "body": "sign.yt ancla mediante OpenTimestamps los hashes SHA-256 del PDF firmado, del registro de auditoría y de la cabeza de la cadena de auditoría. Solo salen de la plataforma los hashes, nunca el documento ni ningún dato personal.\n\nLa prueba suele confirmarse en un bloque de Bitcoin en pocas horas."
        },
        {
          "h2": "Compruébalo tú mismo",
          "body": "El ZIP de evidencias contiene la prueba .ots y la lista de hashes anclados. Verifícalos con el cliente ots de código abierto, contra cualquier nodo de Bitcoin o explorador de bloques, sin cuenta y sin contactar con nosotros.\n\nLa página pública de verificación muestra también el estado del anclaje y la altura del bloque, con un enlace al bloque."
        },
        {
          "h2": "Por qué una blockchain",
          "body": "Una fila de una base de datos puede editarla quien gestiona la base de datos. Un bloque de Bitcoin no puede cambiarlo nadie. Por eso el anclaje es una respuesta sólida e independiente a la pregunta \"¿existía este archivo ese día, exactamente en esta forma?\"."
        }
      ],
      "faq": [
        {
          "q": "¿sign.yt pone mi documento en la blockchain?",
          "a": "No. Solo se anclan hashes. Un hash no revela nada del contenido, pero cualquier cambio en el archivo produce un hash distinto."
        },
        {
          "q": "¿Pago por las transacciones de Bitcoin?",
          "a": "No. Los calendarios públicos de OpenTimestamps agrupan muchos hashes en una sola transacción, así que no hay coste por documento."
        },
        {
          "q": "¿Cuánto tarda la prueba en ser definitiva?",
          "a": "Normalmente unas horas. Hasta entonces el anclaje aparece como pendiente; la prueba se actualiza automáticamente."
        }
      ]
    },
    "post-quantum-seal": {
      "name": "Sello poscuántico",
      "title": "Sello poscuántico: ML-DSA-65 + Ed25519",
      "desc": "Un segundo sello sobre cada documento completado, firmado con ML-DSA-65 y Ed25519, para seguir siendo verificable cuando la computación cuántica rompa las claves actuales.",
      "h1": "Evidencias que sobreviven a la era cuántica",
      "lead": "Las firmas actuales se basan en matemáticas que un ordenador cuántico grande podría romper. sign.yt añade un sello poscuántico híbrido para que tus evidencias conserven su valor durante décadas.",
      "sections": [
        {
          "h2": "Dos algoritmos, ambos deben resistir",
          "body": "El sello firma un manifiesto que vincula el hash del PDF final, la cabeza de la cadena de auditoría y las evidencias de los firmantes. Se firma dos veces: con ML-DSA-65 (FIPS 204, poscuántico) y con Ed25519. El sello solo es válido si ambas firmas se verifican, así que resiste mientras cualquiera de los dos algoritmos siga sin romperse.\n\nEs una evidencia complementaria junto a la firma PAdES, no sustituye a una firma cualificada."
        },
        {
          "h2": "Verificable sin conexión",
          "body": "El archivo del sello forma parte del ZIP de evidencias. Súbelo con el PDF al verificador público o compruébalo sin conexión con las claves públicas publicadas.\n\nUna clave raíz sin conexión certifica o revoca las claves de trabajo. Su parte privada nunca toca un servidor. Una clave de trabajo filtrada puede revocarse, y los sellos de claves revocadas o no certificadas no superan la verificación."
        },
        {
          "h2": "Claves dentro de AWS KMS",
          "body": "En esta plataforma, las claves del sello se guardan en AWS Key Management Service. La firma se realiza dentro del HSM y las claves privadas nunca salen de él; cada firma se comprueba localmente antes de almacenarse.",
          "requires": "aws_kms"
        }
      ],
      "faq": [
        {
          "q": "¿Por qué lo necesito hoy?",
          "a": "Los contratos se conservan durante años. Un atacante puede guardar ahora archivos firmados e intentar falsificarlos cuando los ordenadores cuánticos sean lo bastante potentes. El sello cierra esa brecha."
        },
        {
          "q": "¿Sustituye a la firma PAdES?",
          "a": "No. PAdES sigue siendo la firma estándar que entienden los lectores de PDF. El sello poscuántico es una capa de evidencia adicional e independiente."
        },
        {
          "q": "¿Qué es ML-DSA?",
          "a": "Un algoritmo de firma digital estandarizado por el NIST en FIPS 204, diseñado para resistir ataques de ordenadores cuánticos."
        }
      ]
    },
    "public-verification": {
      "name": "Verificación pública",
      "title": "Verifica un documento firmado con un código QR",
      "desc": "Cada documento firmado lleva un QR y un enlace corto a una página pública: nombres enmascarados, fechas de firma, estado del sello y comparación del archivo.",
      "h1": "Cualquiera puede comprobar un documento en segundos",
      "lead": "Una contraparte, un banco o un tribunal no necesitan una cuenta para confiar en tu documento. Escanean el código QR o arrastran el PDF al verificador.",
      "sections": [
        {
          "h2": "Un código QR en cada certificado",
          "body": "El certificado de finalización de cada documento firmado tiene un código QR y un enlace corto como verify.sign.yt/k3Fq9ZxT2mPa7LwB. Abre una página pública con el estado del documento, cuántas firmas se recogieron y cuándo, el hash del original firmado, el sello de la plataforma, el sello poscuántico y el anclaje en Bitcoin.\n\nLa página usa la marca de la organización que envió el documento."
        },
        {
          "h2": "Comprueba que tu archivo es idéntico",
          "body": "El código QR demuestra que el documento existe en sign.yt. Para demostrar que tu copia no ha cambiado, súbela en la misma página: su hash se compara con el del original firmado y se muestra al lado el informe completo de firmas."
        },
        {
          "h2": "Ningún dato personal en público",
          "body": "Los nombres de los firmantes aparecen enmascarados, por ejemplo \"J. K*****\", y no se muestran correos, teléfonos ni direcciones IP. El enlace es un código aleatorio largo imposible de adivinar, y las consultas tienen límite de frecuencia.\n\nCualquier PDF, firmado con sign.yt o no, puede arrastrarse también al verificador para ver si sus firmas están intactas."
        }
      ],
      "faq": [
        {
          "q": "¿Quien comprueba necesita una cuenta?",
          "a": "No. Tanto la página del QR como el verificador de PDF son públicos."
        },
        {
          "q": "¿Qué revela la página pública?",
          "a": "El estado, los nombres enmascarados de los firmantes con sus fechas de firma, el hash final y el estado del sello y del anclaje. No el contenido del documento."
        },
        {
          "q": "¿Los enlaces pueden usar mi propio dominio?",
          "a": "Por defecto apuntan a verify.sign.yt. Llevarlos a tu dominio forma parte del complemento de pago Personalización completa."
        }
      ]
    },
    "sms-email-verification": {
      "name": "Verificación por SMS y correo",
      "title": "Verificación de firmantes por SMS y correo",
      "desc": "Confirma quién firma con un código de un solo uso enviado por correo, por SMS o por ambos. Caduca a los 10 minutos y cada comprobación queda en la auditoría.",
      "h1": "Sabe quién firmó, no solo qué enlace se pulsó",
      "lead": "Añade un paso con código de un solo uso antes de firmar. El destinatario demuestra que tiene acceso a su buzón, a su teléfono o a ambos, y el resultado queda registrado como evidencia.",
      "sections": [
        {
          "h2": "Elige el nivel en cada documento",
          "body": "En cada documento decides lo estricta que debe ser la verificación: ninguna para un simple acuse de recibo, un código por correo para los contratos del día a día, un código por SMS para acuerdos de mayor valor, o ambos. Con ambos, el destinatario confirma el código del correo al abrir el documento y el código SMS justo antes de firmar.\n\nEl destinatario nunca necesita una cuenta ni una app. El código llega donde ya está, y la página de firma le guía por el paso."
        },
        {
          "h2": "Seis dígitos, de corta duración, difíciles de adivinar",
          "body": "Los códigos son seis dígitos aleatorios válidos durante 10 minutos. Tras cinco intentos fallidos el código se invalida, se puede pedir uno nuevo pasados 60 segundos y los intentos de verificación por enlace tienen un límite diario.\n\nLos SMS tienen además un límite por número y hora, que protege tu cuenta frente al SMS pumping.\n\nEstos límites se aplican automáticamente, no hay nada que configurar."
        },
        {
          "h2": "SMS a través de BulkGate",
          "body": "Los mensajes de texto salen a través de BulkGate, una pasarela de SMS europea. Puedes definir tu propio nombre de remitente de hasta 11 caracteres, para que el código llegue con tu marca y no desde un número cualquiera.\n\nLos números de teléfono se guardan en formato internacional E.164. Si un mensaje tarda en llegar, el destinatario puede pedir un código nuevo desde la página de firma tras una breve espera, sin tener que contactarte."
        },
        {
          "h2": "Prueba en el certificado de finalización",
          "body": "El método de verificación y la hora exacta en que se confirmó cada código aparecen en el certificado de finalización adjunto al PDF firmado y en el registro de auditoría. Si alguna vez se impugna una firma, puedes demostrar qué canal controlaba el firmante.\n\nLos firmantes que usaron un código se distinguen claramente de los que firmaron sin él."
        }
      ],
      "faq": [
        {
          "q": "¿Cuánto tiempo es válido un código?",
          "a": "10 minutos. Después, el destinatario pide uno nuevo desde la página de firma."
        },
        {
          "q": "¿El destinatario necesita una cuenta?",
          "a": "No. Los destinatarios firman desde el enlace de su correo; el código es el único paso adicional."
        },
        {
          "q": "¿Puedo exigir correo y SMS a la vez?",
          "a": "Sí. El código del correo se comprueba al abrir el documento y el código SMS justo antes de firmar."
        },
        {
          "q": "¿Hay una verificación más fuerte que los códigos?",
          "a": "Sí. La verificación de identidad mediante login.gov.pl, Profil Zaufany (perfil oficial polaco de identidad digital) o firmas cualificadas está disponible donde se haya activado para tu organización."
        }
      ]
    },
    "mobile-signing": {
      "name": "Firma en el móvil",
      "title": "Firma en el móvil, con una invitación personal",
      "desc": "Los destinatarios firman en el móvil con un panel inferior y una barra de acción fija. La invitación llega de un remitente con nombre, con tu asunto y mensaje.",
      "h1": "La mayoría de los firmantes abre el enlace en el móvil",
      "lead": "Por eso la página de firma está pensada para usarse con un pulgar. Y el correo que les lleva hasta allí viene de una persona real, no de un robot no-reply.",
      "sections": [
        {
          "h2": "Un flujo de firma hecho para pantallas pequeñas",
          "body": "En el móvil, el PDF se adapta a la página, se renderiza nítido para la pantalla y se reajusta al girar el teléfono. Toca un campo, o la barra fija de abajo, y los controles de firma se abren en un panel inferior: dibuja o escribe la firma, rellena el campo y sigue.\n\nLa barra muestra siempre lo que queda por hacer, y desaparece cuando un documento ha caducado o se ha cancelado."
        },
        {
          "h2": "Una invitación de una persona",
          "body": "La invitación muestra al remitente por su nombre, por ejemplo \"Anna Nowak vía sign.yt\" o vía tu propio nombre de marca, y las respuestas van directamente a Anna en lugar de a un buzón no-reply. El asunto por defecto indica quién pide la firma y para qué documento.\n\nAntes de enviar puedes cambiar el asunto y añadir un mensaje personal. Funciona con el remitente gestionado y con cualquier proveedor de correo que conectes."
        },
        {
          "h2": "Menos preguntas de \"¿esto es phishing?\"",
          "body": "Un nombre conocido, tu marca y un mensaje escrito para este destinatario hacen que el correo inspire confianza. Quien tenga una duda solo tiene que responder."
        }
      ],
      "faq": [
        {
          "q": "¿El firmante necesita una app?",
          "a": "No. La firma funciona en el navegador del móvil desde el enlace del correo."
        },
        {
          "q": "¿A dónde van las respuestas a la invitación?",
          "a": "A la persona que envió el documento. La cabecera Reply-To se fija con el remitente."
        },
        {
          "q": "¿Puedo escribir mi propio texto de correo?",
          "a": "Sí. Edita el asunto y añade un mensaje personal en el diálogo de envío."
        }
      ]
    },
    "profil-zaufany-wezel-podpisu": {
      "name": "Profil Zaufany y firmas cualificadas",
      "title": "Profil Zaufany, e-dowód y firma cualificada",
      "desc": "Permite a firmantes polacos usar Profil Zaufany, e-dowód o mObywatel vía Węzeł Podpisu, o una firma electrónica cualificada de un prestador de confianza.",
      "h1": "Identidad de nivel oficial para los documentos que la necesitan",
      "lead": "Algunos contratos necesitan algo más que un código. sign.yt se conecta a Węzeł Podpisu (servicio estatal polaco de firma) y a prestadores cualificados de servicios de confianza, para que los firmantes usen las herramientas de identidad que ya tienen.",
      "sections": [
        {
          "h2": "Węzeł Podpisu dentro del flujo de firma",
          "body": "Para cada destinatario puedes elegir un método de Węzeł Podpisu: Profil Zaufany (perfil oficial polaco de identidad digital), e-dowód (DNI electrónico polaco), firma personal (podpis osobisty), mObywatel (app oficial polaca de identidad móvil) o una firma cualificada. El destinatario es redirigido al flujo oficial, firma allí, y sign.yt guarda el PDF firmado y pasa el documento al siguiente firmante.\n\nEl orden de firma en paralelo o secuencial funciona igual que con las firmas normales."
        },
        {
          "h2": "Firmas electrónicas cualificadas",
          "body": "Una firma cualificada (QES) es el nivel de eIDAS equivalente a una firma manuscrita. sign.yt admite dos vías: firma remota en un prestador cualificado mediante la API del Cloud Signature Consortium, o subida, en la que el firmante firma el PDF con cualquier herramienta QES y lo vuelve a subir.\n\nLa firma remota funciona con prestadores que ofrecen un endpoint CSC v2. La vía de subida funciona con tarjetas inteligentes, certificados en la nube y otras herramientas cualificadas, así que el firmante no depende de un único proveedor."
        },
        {
          "h2": "Las firmas anteriores quedan intactas",
          "body": "Las firmas cualificadas se añaden de forma incremental a la última revisión del documento. Los archivos subidos solo se aceptan si amplían exactamente los bytes que envió sign.yt, y el certificado de firma debe figurar como cualificado en la lista de confianza de la UE.\n\nEso significa que puedes combinar métodos en un mismo documento: una parte firma con Profil Zaufany, otra con un certificado cualificado y una tercera con una firma normal de sign.yt, mientras el registro de auditoría recoge cada paso."
        }
      ],
      "faq": [
        {
          "q": "¿Necesito una firma cualificada?",
          "a": "Solo cuando la ley exige forma escrita o tu contraparte lo pide. Para la mayoría de los acuerdos comerciales basta con una firma electrónica normal con verificación. Consulta a tu abogado en casos concretos."
        },
        {
          "q": "¿El firmante paga algo?",
          "a": "Profil Zaufany, e-dowód y mObywatel son herramientas públicas propias del firmante. Los certificados cualificados los emiten los prestadores de servicios de confianza en sus propias condiciones."
        },
        {
          "q": "¿Está disponible en todas las cuentas?",
          "a": "Węzeł Podpisu y las conexiones con prestadores cualificados se activan por organización. Escribe a sales@sign.yt para configurarlos."
        },
        {
          "q": "¿Qué herramientas cualificadas funcionan con la vía de subida?",
          "a": "Cualquier herramienta que genere una firma PAdES cualificada reconocida en la lista de confianza de la UE, por ejemplo una tarjeta inteligente o un certificado en la nube."
        }
      ]
    },
    "email-delivery-providers": {
      "name": "Envío de correo",
      "title": "Envía correos de firma desde tu propio dominio",
      "desc": "Envía invitaciones de firma por tu propio SMTP, Microsoft 365 (Graph API), Resend, Amazon SES o Mailgun, con comprobación de SPF, DKIM y DMARC incluida.",
      "h1": "Correos de firma desde tu dirección, con tu proveedor",
      "lead": "Las invitaciones que llegan desde tu propio dominio se abren. Conecta el servicio de correo que ya usas y sign.yt enviará cada notificación a través de él.",
      "sections": [
        {
          "h2": "Elige el proveedor en el que ya confías",
          "body": "Empieza con el envío gestionado, sin nada que configurar. Cuando quieras, cambia tu organización a tu propio servidor SMTP, Microsoft 365 mediante Graph API, tu propia cuenta de Resend, Amazon SES o Mailgun.\n\nLas credenciales del proveedor se guardan cifradas, nunca en texto plano.\n\nCada organización elige un proveedor, así que todas las invitaciones, recordatorios y correos de finalización salen por la misma vía."
        },
        {
          "h2": "Microsoft 365 sin contraseñas SMTP",
          "body": "Con Graph API, sign.yt envía como un buzón de tu tenant de Microsoft 365 usando un registro de aplicación de Azure con el permiso Mail.Send. Sin contraseña SMTP que guardar y sin autenticación básica que mantener activada en tu tenant.\n\nTú eliges la dirección de envío, por ejemplo contratos@tuempresa.es, para que las respuestas de los destinatarios lleguen a un buzón real que tu equipo ya revisa."
        },
        {
          "h2": "Registros DNS comprobados por ti",
          "body": "La pantalla de ajustes muestra los registros SPF, DKIM y DMARC que necesita tu dominio de envío para el proveedor elegido, los consulta en tiempo real e indica cuáles están configurados. Menos invitaciones acaban en spam.\n\nLos registros DKIM de Amazon SES se obtienen automáticamente para tu dominio, y para los demás proveedores la pantalla te dice exactamente qué valores copiar en tu panel de DNS."
        },
        {
          "h2": "Los rebotes no pasan desapercibidos",
          "body": "Con el envío gestionado, los correos rebotados o marcados como spam marcan al destinatario como rebotado, se anotan en el registro de auditoría y disparan un webhook recipient.bounced, para que corrijas la dirección en lugar de esperar una firma que nunca llegará.\n\nLa misma información del rebote aparece en el documento, junto al destinatario. Te enteras en cuanto el proveedor lo notifica, no cuando ya ha vencido el plazo de firma."
        }
      ],
      "faq": [
        {
          "q": "¿Qué proveedores de correo se admiten?",
          "a": "Envío gestionado, SMTP, Microsoft 365 mediante Graph API, Resend con tu propia cuenta, Amazon SES y Mailgun."
        },
        {
          "q": "¿Qué puertos SMTP puedo usar?",
          "a": "Los puertos 25, 465, 587 y 2525, con TLS."
        },
        {
          "q": "¿Los equipos pueden usar nombres de remitente distintos?",
          "a": "Sí. El nombre de remitente puede definirse por equipo en la marca del equipo."
        },
        {
          "q": "¿Tengo que configurar algo para empezar?",
          "a": "No. El envío gestionado funciona desde el primer momento; tu propio proveedor es opcional."
        }
      ]
    },
    "document-templates": {
      "name": "Plantillas de documentos",
      "title": "Plantillas de firma electrónica y enlaces públicos",
      "desc": "Guarda contratos como plantillas con firmantes y campos. Reutilízalas en segundos, cambia el PDF si cambia el texto o comparte un enlace público de firma.",
      "h1": "Prepara un contrato una vez, envíalo cien veces",
      "lead": "Las plantillas guardan juntos el PDF, los roles de los firmantes y la posición de los campos. Volver a enviar el mismo acuerdo lleva unos clics en lugar de prepararlo de nuevo.",
      "sections": [
        {
          "h2": "De cualquier documento a una plantilla",
          "body": "Sube un PDF y coloca campos de firma, fecha, texto y casillas, o convierte en plantilla un documento que ya enviaste. Los roles de los firmantes y la posición de los campos se guardan con ella.\n\nCuando cambia el texto del contrato pero no la maquetación, sustituye el PDF y conserva cada campo donde estaba.\n\nLas plantillas se organizan por categorías, así que tu equipo encuentra rápido el acuerdo correcto."
        },
        {
          "h2": "Enlaces públicos y códigos QR",
          "body": "Activa un enlace público en una plantilla y cualquiera que tenga el enlace podrá rellenarla y firmarla, por ejemplo un formulario de alta o un consentimiento de marketing. sign.yt genera además un código QR para el enlace, listo para un folleto o un mostrador de recepción.\n\nUn enlace público puede desactivarse en cualquier momento y limitarse por fecha de caducidad o número de usos. Cada copia firmada es un documento independiente con su propio registro de auditoría."
        },
        {
          "h2": "Un punto de partida para pymes polacas",
          "body": "Un catálogo inicial propone roles, orden de firma, nivel de verificación y disposición de campos para documentos habituales en oficinas virtuales, despachos de abogados, contabilidad, RR. HH. y RGPD. Tú subes tu propio PDF y el modelo completa el resto.\n\nEs un comienzo más rápido que un editor vacío, sobre todo para equipos que preparan sus primeras plantillas. Las categorías reflejan los sectores con los que más trabajamos."
        },
        {
          "h2": "Campos colocados por ti",
          "body": "Si lo pides, sign.yt puede analizar una página de tu PDF y sugerir dónde van los campos de firma, fecha y texto. Revisas la sugerencia, la ajustas y la guardas.\n\nLa detección es opcional y solo se ejecuta cuando la pides. La disposición final siempre la decides tú, y puedes mover, redimensionar o eliminar cualquier campo sugerido antes de que el documento llegue a los destinatarios."
        }
      ],
      "faq": [
        {
          "q": "¿Puedo actualizar una plantilla sin rehacer los campos?",
          "a": "Sí. Sustituye el PDF y se conservan las posiciones de los campos existentes."
        },
        {
          "q": "¿Quién puede firmar mediante un enlace público?",
          "a": "Cualquiera que tenga el enlace, mientras esté activado. Puedes desactivarlo en cualquier momento."
        },
        {
          "q": "¿La detección automática de campos está siempre activa?",
          "a": "No. Solo se ejecuta cuando pulsas el botón en el editor de campos."
        }
      ]
    },
    "bulk-send-campaigns": {
      "name": "Envío masivo y campañas",
      "title": "Envío masivo de documentos a firmar desde CSV",
      "desc": "Envía un documento a cientos de personas desde un CSV. Envío dosificado, pausa y reanudación, recordatorios, exportación a CSV y modo de campaña pública con QR.",
      "h1": "Un documento, cientos de firmantes, cero copiar y pegar",
      "lead": "Actualizaciones anuales de políticas, nuevas condiciones del servicio, renovación de consentimientos. Las campañas envían a cada persona su propio documento y siguen cada firma en un solo lugar.",
      "sections": [
        {
          "h2": "Sube un CSV, revísalo, envía",
          "body": "Prepara un CSV con correo, nombre y teléfono, y sign.yt muestra una vista previa antes de enviar nada. Cada destinatario recibe un documento independiente, así que la firma o el rechazo de una persona nunca bloquea a las demás.\n\nUsos típicos son la renovación anual de NDA, condiciones actualizadas para todos los clientes o un nuevo consentimiento para cada inquilino. La vista previa señala los problemas del archivo, como una columna de correo que falta, antes de enviar nada."
        },
        {
          "h2": "Envío a un ritmo seguro",
          "body": "Las campañas se envían en segundo plano a un ritmo por organización compartido con los recordatorios, así que una lista grande no satura tu buzón ni supera los límites de tu proveedor. Puedes pausar, reanudar o cancelar una campaña en curso en cualquier momento.\n\nEl envío se gestiona fila a fila, así que aunque haya varios procesos en marcha nadie recibe el mismo documento dos veces."
        },
        {
          "h2": "Seguimiento sin hojas de cálculo",
          "body": "La vista de la campaña muestra quién ha firmado y quién no. Envía un recordatorio a todos los pendientes, reintenta los envíos fallidos y exporta los resultados a CSV o las evidencias a JSON para tus archivos.\n\nEn las campañas públicas puedes añadir además una lista de personas que esperas que firmen y ver de un vistazo quién falta."
        },
        {
          "h2": "Campañas públicas con código QR",
          "body": "¿Necesitas firmas de personas que aún no tienes en una lista, como asistentes a un evento o nuevos inquilinos? Una campaña pública te da un enlace y un código QR, y cada visitante firma su propia copia.\n\nCada copia lleva sus propias firmas, su verificación y su registro de auditoría, exactamente igual que un documento enviado desde una lista."
        }
      ],
      "faq": [
        {
          "q": "¿Qué columnas necesita el CSV?",
          "a": "Correo, nombre y, opcionalmente, teléfono, que se usa para la verificación por SMS."
        },
        {
          "q": "¿Puedo detener una campaña a medias?",
          "a": "Sí. Paúsala y reanúdala, o cancela los envíos restantes."
        },
        {
          "q": "¿Los recordatorios cuentan para el ritmo de envío?",
          "a": "Sí. Campañas y recordatorios comparten un mismo ritmo por organización, lo que mantiene el envío estable."
        }
      ]
    },
    "smart-documents-gus-vies": {
      "name": "Documentos inteligentes",
      "title": "Generador de contratos con NIP, GUS y VIES",
      "desc": "Genera contratos desde plantillas con variables. Introduce un NIP y los datos de la empresa se rellenan desde GUS, la lista blanca de IVA y VIES. Y a firmar.",
      "h1": "Contratos que se rellenan solos a partir de un número fiscal",
      "lead": "Los documentos inteligentes convierten el texto de tu contrato en un formulario. Escribe un NIP, trae los datos de la empresa de los registros públicos y genera PDF listos para firmar.",
      "sections": [
        {
          "h2": "Datos de empresa de registros públicos",
          "body": "Introduce un NIP polaco y sign.yt combina los datos de GUS (REGON, KRS, forma jurídica, dirección) con la lista blanca de IVA del Ministerio de Finanzas polaco (situación de IVA y cuentas bancarias). Para otros países de la UE usa VIES.\n\nSi una fuente no está disponible, las demás rellenan lo que pueden y ves un aviso en lugar de un error."
        },
        {
          "h2": "Plantillas con lógica real",
          "body": "Redacta el contrato en el editor integrado o importa un archivo .docx en el que los marcadores entre corchetes o dobles llaves se convierten en variables. Las variables tienen tipos como importe, fecha, NIP, IBAN o lista, y puedes añadir secciones condicionales, filas repetidas y valores calculados.\n\nLos valores calculados usan un lenguaje de expresiones seguro, así que los totales, los importes netos y brutos o las fechas de vencimiento se calculan solos. El documento terminado se genera en PDF y pasa por el flujo de firma habitual."
        },
        {
          "h2": "Formato polaco integrado",
          "body": "Los importes se formatean al estilo polaco y pueden escribirse en letras, las fechas siguen las convenciones polacas y los pagos pueden dividirse automáticamente en plazos. Se acabaron los campos \"słownie\" a mano.\n\nLos valores se comprueban según su tipo antes de generar el documento, así que un NIP o un IBAN no válido se detecta en el formulario y no en el contrato firmado. Cada PDF generado parece un documento preparado a mano por tu oficina."
        },
        {
          "h2": "Un formulario, un paquete completo",
          "body": "Agrupa varios documentos en un paquete, como un contrato, un anexo y una cláusula RGPD. Las variables compartidas se rellenan una vez, y sign.yt genera y envía todos los documentos juntos.\n\nEl formulario del paquete muestra cada variable una sola vez, aunque aparezca en varios documentos. Así se eliminan los errores de copiar y pegar entre el contrato principal y sus anexos, y el destinatario firma todo el conjunto en un solo flujo."
        }
      ],
      "faq": [
        {
          "q": "¿Qué registros se usan?",
          "a": "GUS BIR (base de datos REGON), la lista blanca de IVA del Ministerio de Finanzas polaco y el sistema VIES de la UE."
        },
        {
          "q": "¿Los firmantes pueden rellenar los datos de su propia empresa?",
          "a": "Sí. En la página de firma, un destinatario puede introducir su NIP y obtener los datos de su empresa de los registros."
        },
        {
          "q": "¿Puedo importar mis contratos de Word existentes?",
          "a": "Sí. Sube un archivo .docx y los marcadores como [NOMBRE CLIENTE] o {{client.name}} se convierten en variables."
        }
      ]
    },
    "api-webhooks": {
      "name": "API y webhooks",
      "title": "REST API de firma electrónica con webhooks",
      "desc": "Crea, envía y sigue documentos desde tu software. Claves de API con permisos, webhooks firmados con HMAC y reintentos automáticos, y una REST API versionada.",
      "h1": "Integra la firma electrónica en el software que ya usas",
      "lead": "Tu CRM, tu ERP o tu portal de clientes pueden crear documentos, enviarlos a firmar y reaccionar en cuanto se firman, sin que nadie abra sign.yt.",
      "sections": [
        {
          "h2": "Una REST API versionada",
          "body": "Todo está bajo api.sign.yt/v1 y habla JSON: documentos, destinatarios, campos, plantillas y webhooks. Crea un documento desde una plantilla, envíalo y descarga el PDF firmado en unas pocas llamadas.\n\nCada solicitud está limitada a la organización propietaria de la clave de API, así que una integración nunca puede ver los documentos de otro cliente. Los errores llegan como JSON estructurado con códigos de estado claros, lo que mantiene sencillo el código cliente."
        },
        {
          "h2": "Claves de API con permisos",
          "body": "Cada organización crea sus propias claves de API y concede a cada clave solo los permisos que necesita, lectura o escritura por área. Las claves se guardan como hash, tras crearlas solo se muestra el prefijo y cada clave está limitada a 100 solicitudes por segundo.\n\nRevoca una clave en cualquier momento y las integraciones que la usan se detienen al instante. La fecha del último uso te ayuda a detectar claves que ya nadie necesita."
        },
        {
          "h2": "Webhooks en los que puedes confiar",
          "body": "Suscríbete a eventos como document.sent, document.viewed, document.signed, document.completed, document.rejected, document.expired y recipient.bounced. Cada entrega se firma con HMAC-SHA256 sobre una marca de tiempo y el cuerpo sin procesar, para que puedas verificar que viene de sign.yt y rechazar reenvíos.\n\nLos endpoints se vuelven a comprobar antes de cada entrega para bloquear solicitudes a direcciones internas. Cada solicitud lleva además en sus cabeceras el nombre del evento y un ID de entrega, para que enrutes y deduplices eventos fácilmente."
        },
        {
          "h2": "Reintentos incluidos",
          "body": "Si tu endpoint no responde, sign.yt reintenta a los 1 minuto, 5 minutos, 30 minutos, 2 horas y 12 horas. Cada entrega tiene un ID estable para deduplicar, y las entregas fallidas se conservan para que nada se pierda en silencio.\n\nCada intento se firma de nuevo con una marca de tiempo nueva, así que incluso un reintento horas después supera en tu lado la comprobación de la ventana de reenvío."
        }
      ],
      "faq": [
        {
          "q": "¿Cómo me autentico?",
          "a": "Envía tu clave de API como token Bearer en la cabecera Authorization."
        },
        {
          "q": "¿Cómo verifico un webhook?",
          "a": "Calcula el HMAC-SHA256 de la cabecera de marca de tiempo, un punto y el cuerpo sin procesar con tu secreto de webhook, y compáralo con la cabecera X-Signyt-Signature."
        },
        {
          "q": "¿Qué pasa si mi endpoint falla?",
          "a": "sign.yt reintenta cinco veces durante unas 15 horas y después marca la entrega como fallida."
        },
        {
          "q": "¿Qué planes incluyen acceso a la API?",
          "a": "Las claves de API y los webhooks forman parte del plan Business y superiores."
        }
      ]
    },
    "audit-trail": {
      "name": "Registro de auditoría",
      "title": "Auditoría a prueba de manipulación para firmas",
      "desc": "Cada acción queda en un registro de auditoría de solo adición, encadenado por hash. Las firmas se encadenan por documento y cada PDF lleva su certificado.",
      "h1": "Un registro de auditoría que muestra si alguien intentó reescribir la historia",
      "lead": "Quién lo envió, quién lo abrió, quién se verificó y quién firmó, con direcciones IP y marcas de tiempo. Los registros se encadenan con SHA-256, así que una entrada eliminada o alterada rompe la cadena.",
      "sections": [
        {
          "h2": "Registro solo de adición, encadenado por hash",
          "body": "Cada entrada de auditoría guarda el hash de la entrada anterior en la cadena de tu organización. Las entradas solo se añaden, nunca se modifican ni se eliminan, algo que impone la propia base de datos, y cada cliente tiene su propia cadena independiente.\n\nSi alguien eliminara o editara un registro directamente en la base de datos, la cadena dejaría de verificarse. Los eventos cubren todo el ciclo de vida, desde el inicio de sesión y los cambios de ajustes hasta el envío, la visualización y la firma."
        },
        {
          "h2": "Firmas enlazadas entre sí",
          "body": "Cada firma de un documento apunta al hash de la anterior. Antes de marcar un documento como completado, sign.yt recorre toda la cadena y se niega a completarlo si algo no coincide.\n\nUna cadena rota queda anotada en el registro de auditoría, así que el problema es visible en lugar de quedar sellado en silencio en un PDF terminado. Junto con las firmas PAdES, esto da dos formas independientes de demostrar el orden de los hechos."
        },
        {
          "h2": "Consentimiento explícito y anclaje en Bitcoin",
          "body": "Antes de firmar, cada firmante marca una declaración de consentimiento. Su texto, su idioma y su hash se guardan con la firma y en el registro de auditoría, para que puedas mostrar exactamente qué aceptó la persona.\n\nCuando se completa un documento, sign.yt ancla en Bitcoin mediante OpenTimestamps los hashes del PDF firmado, del registro de auditoría y de la cabeza de la cadena. La prueba demuestra que los archivos existían en la hora de ese bloque y puede comprobarse con la herramienta ots de código abierto, sin depender de sign.yt."
        },
        {
          "h2": "Certificado de finalización en el PDF",
          "body": "El PDF firmado termina con un certificado de finalización: nombre, correo, rol y método de verificación de cada firmante, cuándo se envió, se vio y se firmó el documento, la dirección IP y el navegador. Tus evidencias viajan con el archivo.\n\nComo el certificado forma parte del PDF, acompaña a cada copia del documento, dondequiera que se guarde o se reenvíe. Su código QR y su enlace corto abren una página pública de verificación del documento."
        },
        {
          "h2": "Encuentra y exporta lo que necesitas",
          "body": "Filtra el registro de auditoría de la organización por tipo de evento, o descarga en CSV el historial completo de un documento para tu abogado o tu auditor.\n\nEl ZIP de evidencias lo reúne todo en una descarga: el PDF firmado, un registro de auditoría independiente, la prueba de OpenTimestamps, el certificado del sello de la plataforma, un manifiesto de hashes SHA-256 y un README que explica cómo verificar cada parte. Los firmantes también pueden descargarlo.\n\nLas entradas registran el actor, el correo y la dirección IP, el objeto y los detalles del evento. Los administradores de la organización solo ven su propia organización, y el registro está paginado para que incluso los historiales largos se consulten y busquen con rapidez."
        }
      ],
      "faq": [
        {
          "q": "¿Se puede editar o eliminar una entrada de auditoría?",
          "a": "No. En el registro solo se añaden entradas, y cualquier cambio hecho fuera de la aplicación rompe la cadena de hashes."
        },
        {
          "q": "¿Qué contiene el certificado de finalización?",
          "a": "La identidad del firmante, el método de verificación, las horas de envío, visualización y firma, la dirección IP y el agente de usuario."
        },
        {
          "q": "¿Puedo exportar el registro de auditoría?",
          "a": "Sí. El historial de cada documento puede descargarse en CSV, y el paquete completo de evidencias como ZIP."
        },
        {
          "q": "¿Qué es el anclaje en Bitcoin?",
          "a": "Una prueba de OpenTimestamps de que los hashes del documento existían cuando se minó un bloque concreto de Bitcoin. Se confirma en pocas horas y puede verificarse con independencia de sign.yt."
        }
      ]
    },
    "gdpr-rodo-compliance": {
      "name": "Cumplimiento del RGPD",
      "title": "Firma electrónica conforme al RGPD y cifrada",
      "desc": "Exportación y supresión de datos gestionadas en el producto, PDF cifrados en reposo con una clave distinta por organización y aislamiento entre clientes.",
      "h1": "Privacidad gestionada en el producto, no en una hoja de cálculo",
      "lead": "Las solicitudes de los interesados, el cifrado y el aislamiento entre clientes forman parte de sign.yt. Dedicas menos tiempo al papeleo del RGPD y conservas los documentos firmados que la ley te obliga a guardar.",
      "sections": [
        {
          "h2": "Exportación a petición",
          "body": "Los usuarios pueden descargar todos sus datos en un archivo ZIP: perfil, organizaciones, metadatos de documentos y firmas, contactos y las entradas de auditoría que se refieren a ellos. Las personas sin cuenta, como los destinatarios, pueden pedir sus datos por correo y recibir un enlace de descarga de un solo uso.\n\nLas solicitudes públicas tienen límite de frecuencia por correo y por dirección IP, para que nadie pueda saturar el sistema."
        },
        {
          "h2": "Supresión que conserva las evidencias",
          "body": "Cuando un usuario pide ser olvidado, sign.yt lo anonimiza: los nombres y las direcciones de correo se sustituyen por etiquetas opacas, se revocan las sesiones y se eliminan los métodos de inicio de sesión. Los documentos firmados se conservan, porque borrarlos rompería el registro de auditoría y los derechos de las demás partes.\n\nLas cuentas de superadministrador requieren una confirmación adicional, que protege frente a borrados accidentales."
        },
        {
          "h2": "Cifrado en reposo, por organización",
          "body": "Los PDF subidos y firmados se cifran con AES-256-GCM usando una clave derivada por separado para cada organización. Las credenciales de los proveedores de correo y SMS se guardan con cifrado a nivel de campo, nunca en texto plano.\n\nCada archivo está vinculado a su organización y a su documento, así que un blob copiado al lugar equivocado no puede descifrarse allí. Las claves nunca se guardan en la base de datos junto a los datos que protegen."
        },
        {
          "h2": "Los clientes nunca se mezclan",
          "body": "Cada consulta se limita a la organización a la que pertenece. Las solicitudes en un dominio propio se resuelven a exactamente una organización, y las cadenas de auditoría se mantienen por cliente.\n\nEste aislamiento es lo que permite que muchas empresas, y muchas marcas, compartan una plataforma sin compartir datos. Los contratos de tus clientes solo son visibles para las personas que invitas a tu organización."
        }
      ],
      "faq": [
        {
          "q": "¿Los destinatarios sin cuenta pueden ejercer sus derechos?",
          "a": "Sí. Envían una solicitud con su dirección de correo y reciben un enlace por correo. La respuesta nunca revela si la dirección existe en el sistema."
        },
        {
          "q": "¿Se borran los documentos firmados al suprimir los datos?",
          "a": "No. Los datos personales se anonimizan, pero los documentos firmados se conservan para preservar las evidencias y los derechos de las demás partes."
        },
        {
          "q": "¿Mis datos están cifrados?",
          "a": "Sí. Los documentos se cifran en reposo con AES-256-GCM y una clave distinta por organización."
        }
      ]
    }
  },
  "groups": {
    "trust": "Confianza y aspectos legales",
    "brand": "Tu marca",
    "docs": "Trabajo con documentos",
    "dev": "Para desarrolladores"
  },
  "plans": {
    "starter": [
      "50 documentos / mes",
      "3 usuarios, 1 equipo",
      "Verificación con código por correo",
      "\"Powered by sign.yt\" en las páginas de inicio de sesión"
    ],
    "professional": [
      "500 documentos / mes",
      "10 usuarios, 5 equipos",
      "50 códigos SMS / mes",
      "Dominio propio, sin marca de agua"
    ],
    "business": [
      "Documentos, usuarios y equipos ilimitados",
      "SSO",
      "Tu propio proveedor de correo",
      "REST API y webhooks"
    ],
    "enterprise": [
      "Todo lo de Business",
      "SAML",
      "SLA y soporte dedicado",
      "Opción on-premise"
    ]
  },
  "pricingFaq": [
    {
      "q": "¿Los precios son netos o brutos?",
      "a": "Todos los precios son mensuales y netos, en PLN. El IVA polaco (23%) se añade en la factura."
    },
    {
      "q": "¿Qué pasa si superamos el límite de documentos?",
      "a": "Los planes de pago siguen funcionando. Los documentos que superan el límite mensual se facturan a 2 PLN cada uno en tu siguiente factura, y los administradores reciben un aviso por correo al llegar al 80% y al 100% del límite."
    },
    {
      "q": "¿Emitís facturas con IVA?",
      "a": "Sí. Cada pago va acompañado de una factura con IVA a nombre de tu empresa, con tu número fiscal y la dirección de facturación de los ajustes de la organización."
    },
    {
      "q": "¿Podemos cambiar de plan más adelante?",
      "a": "Sí. Puedes cambiar de plan en cualquier momento en el portal de facturación, accesible desde tus ajustes de facturación. Los límites se adaptan al nuevo plan."
    },
    {
      "q": "¿Podemos añadir más equipos sin pasar a un plan superior?",
      "a": "Se pueden añadir equipos adicionales a un plan de pago por una cuota mensual. Escríbenos y lo configuramos para tu organización."
    },
    {
      "q": "¿Cómo funcionan los precios de Enterprise?",
      "a": "Enterprise se presupuesta según tu volumen y tus necesidades de seguridad y alojamiento, con SAML, SLA y opción on-premise. Escribe a sales@sign.yt."
    }
  ],
  "pricingRows": [
    [
      "Documentos al mes",
      [
        "50",
        "500",
        "Ilimitados",
        "Ilimitados"
      ]
    ],
    [
      "Usuarios",
      [
        "3",
        "10",
        "Ilimitados",
        "Ilimitados"
      ]
    ],
    [
      "Equipos",
      [
        "1",
        "5",
        "Ilimitados",
        "Ilimitados"
      ]
    ],
    [
      "Verificación con código por correo",
      [
        true,
        true,
        true,
        true
      ]
    ],
    [
      "Códigos SMS",
      [
        false,
        "50 / mes",
        true,
        true
      ]
    ],
    [
      "Dominio de firma propio",
      [
        false,
        true,
        true,
        true
      ]
    ],
    [
      "Tu marca, sin \"Powered by\"",
      [
        false,
        true,
        true,
        true
      ]
    ],
    [
      "SSO (Google, Microsoft, OIDC)",
      [
        false,
        false,
        true,
        true
      ]
    ],
    [
      "Tu propio proveedor de correo",
      [
        false,
        false,
        true,
        true
      ]
    ],
    [
      "REST API y webhooks",
      [
        false,
        false,
        true,
        true
      ]
    ],
    [
      "SAML",
      [
        false,
        false,
        false,
        true
      ]
    ],
    [
      "SLA y soporte dedicado",
      [
        false,
        false,
        false,
        true
      ]
    ],
    [
      "Opción on-premise",
      [
        false,
        false,
        false,
        true
      ]
    ]
  ],
  "pages": {
    "home": {
      "title": "sign.yt: firma electrónica en tu propio dominio",
      "desc": "Plataforma de firma electrónica para oficinas, coworkings y asesorías: tu dominio y tu marca, sello PAdES y verificación del firmante por SMS y correo.",
      "h1": "Contratos firmados con tu propio nombre.",
      "lead": "sign.yt gestiona todo el proceso de firma en tu dominio, con tu logotipo y tu remitente de correo. Los firmantes se verifican con un código por correo o SMS, y cada PDF vuelve con sello PAdES y un registro de auditoría completo."
    },
    "hub": {
      "title": "Funciones de firma electrónica: PAdES, API",
      "desc": "Todo lo que hace sign.yt: dominios propios, marca, PAdES, sellos de tiempo, anclaje en Bitcoin, sello poscuántico, verificación QR, API y herramientas RGPD.",
      "h1": "Todo lo que necesitas para firmar documentos online",
      "lead": "Cada una de estas funciones ya existe hoy en el producto. Elige una para ver cómo funciona."
    },
    "pricing": {
      "title": "Precios de firma electrónica: planes en PLN",
      "desc": "Firma electrónica en PLN al mes: {plans}, Enterprise a medida. Precios netos más IVA; cambia o cancela cuando quieras.",
      "h1": "Planes que crecen con tus oficinas",
      "lead": "Precios mensuales netos en PLN. Empieza con poco y sube de plan cuando necesites dominios propios, SSO o la API."
    }
  },
  "labels": {
    "home": "Inicio",
    "features": "Funciones",
    "pricing": "Precios",
    "faq": "Preguntas frecuentes",
    "month": "mes",
    "custom": "A medida",
    "contact": "Contactar con ventas",
    "verify": "Verificar un documento",
    "all": "Todas las funciones",
    "more": "Más funciones",
    "language": "Idioma",
    "breadcrumb": "Ruta de navegación",
    "not_found_title": "Página no encontrada",
    "not_found_lead": "La página que buscas no existe o se ha movido.",
    "back_home": "Volver al inicio"
  },
  "ui": {
    "skip": "Saltar al contenido",
    "nav_label": "Principal",
    "features": "Funciones",
    "pricing": "Precios",
    "faq": "FAQ",
    "verify": "Verificar un documento",
    "developers": "Desarrolladores y API",
    "language": "Idioma",
    "sign_in": "Iniciar sesión",
    "book_demo": "Reservar una demo",
    "dashboard": "Ir al panel",
    "menu": "Menú",
    "footer_blurb": "Firma electrónica en tu propio dominio, con tu marca y tu remitente de correo. Pensada para oficinas, coworkings y asesorías.",
    "footer_product": "Producto",
    "footer_company": "Empresa",
    "footer_legal": "Legal",
    "all_features": "Todas las funciones",
    "contact": "Contacto",
    "status": "Estado",
    "privacy": "Política de privacidad",
    "terms": "Condiciones del servicio",
    "dpa": "Contrato de encargo del tratamiento",
    "subprocessors": "Subencargados",
    "rodo": "Solicitar mis datos (RGPD)",
    "faq_title": "Preguntas frecuentes",
    "cta_title": "Pruébalo con tus propios documentos",
    "cta_lead": "Reserva una demo breve y prepararemos una organización de prueba con tu dominio y tu marca.",
    "not_found_title": "Página no encontrada",
    "not_found_lead": "La página que buscas no existe o se ha movido.",
    "back_home": "Volver al inicio",
    "home": "Inicio",
    "breadcrumb": "Ruta de navegación",
    "feature_kicker": "Función",
    "more_features": "Más funciones",
    "see_all": "Ver todas las funciones",
    "see_pricing": "Ver precios",
    "read_more": "Leer más",
    "per_month": "PLN / mes",
    "net_note": "netos + IVA",
    "custom_price": "A medida",
    "custom_note": "Precio según tu volumen",
    "recommended": "Recomendado",
    "choose_plan": "Elegir",
    "contact_sales": "Contactar con ventas",
    "compare_kicker": "Lado a lado",
    "compare_title": "Compara los planes",
    "feature_col": "Función",
    "included": "Incluido",
    "not_included": "No incluido",
    "scroll_hint": "Desliza hacia un lado para comparar todos los planes",
    "dev_title": "Empieza a integrar",
    "dev_lead": "Referencia completa de la API, una guía rápida de cinco minutos y SDK oficiales para Python y TypeScript.",
    "dev_reference": "Referencia de la API",
    "dev_quickstart": "Guía rápida",
    "dev_sdks": "SDK",
    "copy": "Copiar",
    "copied": "Copiado",
    "pricing_faq_title": "Preguntas sobre facturación",
    "not_found_try": "Quizá buscabas",
    "lang_suggest": "Esta página también está disponible en español.",
    "lang_switch": "Leer en español",
    "lang_dismiss": "No, gracias"
  },
  "examples": {
    "api": {
      "log": "Entregas de webhooks",
      "target": "crm.example.pl/hooks/sign"
    },
    "audit": {
      "ok": "Cadena intacta",
      "rows": [
        [
          "Documento creado",
          "anna@example.pl"
        ],
        [
          "Enviado al firmante",
          "jan@example.pl"
        ],
        [
          "Abierto",
          "83.12.x.x · Firefox"
        ],
        [
          "Código SMS verificado",
          "+48 600 *** 412"
        ],
        [
          "Firmado",
          "83.12.x.x · 09:33:10"
        ]
      ]
    },
    "bitcoin": {
      "file": "firmado.pdf",
      "block": "Bloque",
      "ok": "Anclado"
    },
    "bulk": {
      "docs": "4 documentos",
      "progress": "2 de 4 firmados",
      "st": {
        "signed": "Firmado",
        "viewed": "Visto",
        "sent": "Enviado"
      }
    },
    "domains": {
      "title": "Dominio propio",
      "type": "Tipo",
      "name": "Nombre",
      "target": "Apunta a",
      "status": "Estado",
      "verified": "Verificado",
      "tls": "Certificado TLS emitido",
      "doc": "Contrato de arrendamiento, oficina 2.14",
      "sign": "Firmar",
      "host": "sign.tuempresa.es"
    },
    "templates": {
      "search": "Buscar plantillas",
      "fields": "campos",
      "items": [
        [
          "Arrendamiento de oficina",
          "Arrendamiento",
          9
        ],
        [
          "NDA mutuo",
          "Legal",
          4
        ],
        [
          "Anexo al contrato",
          "Arrendamiento",
          5
        ],
        [
          "Consentimiento de marketing",
          "RGPD",
          2
        ]
      ]
    },
    "email": {
      "title": "Proveedor de correo",
      "from": "Remitente",
      "test": "Correo de prueba entregado",
      "managed": "Gestionado"
    },
    "gdpr": {
      "req": "Solicitud del interesado",
      "exp": "Exportar",
      "erase": "Suprimir",
      "steps": [
        "Identidad confirmada",
        "Datos recopilados de 3 documentos",
        "Contratos firmados conservados, plazo legal"
      ],
      "zip": "Descargar ZIP",
      "foot": "Alojamiento en la UE · AES-256-GCM en reposo"
    },
    "mobile": {
      "title": "Tu firma",
      "sign": "Firmar",
      "left": "Queda 1 de 2 campos"
    },
    "pades": {
      "valid": "Firma válida",
      "signer": "Firmado por",
      "format": "Formato",
      "time": "Hora de firma",
      "intact": "Sin modificar desde la firma",
      "file": "contrato-firmado.pdf"
    },
    "pz": {
      "steps": [
        "Revisar",
        "Método",
        "Firmar"
      ],
      "choose": "Elige cómo firmar",
      "opts": [
        [
          "Profil Zaufany",
          "Gratis, vía login.gov.pl"
        ],
        [
          "e-dowód",
          "DNI polaco con NFC"
        ],
        [
          "Firma electrónica cualificada",
          "Prestador de servicios de confianza"
        ]
      ],
      "btn": "Firmar con Profil Zaufany",
      "note": "Se te redirigirá a Węzeł Podpisu"
    },
    "smart": {
      "nip": "NIP del arrendatario",
      "fetched": "Encontrado en los registros",
      "name": "Razón social",
      "addr": "Domicilio social",
      "vat": "Situación de IVA",
      "active": "Contribuyente de IVA activo, IVA UE válido",
      "gen": "Generar contrato"
    },
    "sms": {
      "title": "Confirma que eres tú",
      "sent": "Hemos enviado un código de 6 dígitos a",
      "email": "Correo",
      "resend": "Reenviar código en 0:42",
      "go": "Continuar al documento"
    },
    "brand": {
      "doc": "Contrato de servicios",
      "sign": "Firmar",
      "from": "de"
    }
  },
  "home": {
    "get_started": "Empezar",
    "hero_line1": "Contratos firmados",
    "hero_line2": "con tu propio nombre.",
    "hero_sub": "sign.yt gestiona todo el proceso de firma en tu dominio, con tu logotipo y tu remitente de correo. Los firmantes se verifican con un código por correo o SMS, y cada PDF vuelve con sello PAdES y un registro de auditoría completo.",
    "cta_demo_sign_in": "Iniciar sesión",
    "cta_view_signing": "Ver página de firma",
    "cta_see_pricing": "Ver precios",
    "cta_start_free": "Empezar prueba gratis",
    "nav_features": "Funciones",
    "nav_pricing": "Precios",
    "nav_plans": "Planes",
    "nav_contact": "Contacto",
    "cta_book_demo": "Reservar una demo",
    "plans_title": "Paga por documentos, no por firmantes.",
    "plans_sub": "Precios en PLN netos + IVA, facturados mensualmente. Los firmantes nunca pagan ni necesitan una cuenta.",
    "plans_note": "Todos los planes incluyen PDF con sello PAdES, alojamiento en la UE y un registro de auditoría completo.",
    "tier_contact_cta": "Contáctanos",
    "contact_title": "Hablemos de tus documentos",
    "contact_sub": "Cuéntanos qué firmas, cuántos documentos al mes aproximadamente y qué dominio quieres usar. Te responderemos con una cita para la demo y una organización de prueba.",
    "contact_email_cta": "Escríbenos",
    "contact_response_time": "Solemos responder en un día laborable.",
    "footer_company": "Empresa",
    "features_title": "Todo lo que necesita un equipo de firma, nada que sobre",
    "features_sub": "Pensado para equipos que envían contratos a diario y quieren que lleven su propia imagen.",
    "feature_custom_domains_title": "Dominios propios",
    "feature_custom_domains_body": "Cada cliente en su propio subdominio con su marca. Una función clave que no ofrece ningún gran competidor.",
    "feature_pades_title": "Firmas PAdES",
    "feature_pades_body": "PDF firmados en formato PAdES (ETSI EN 319 142).",
    "feature_dev_first_title": "Pensado para desarrolladores",
    "feature_dev_first_body": "REST API completa, webhooks, SDK y servidor MCP para agentes de IA.",
    "feature_wezel_title": "Preparado para Węzeł Podpisu",
    "feature_wezel_body": "Integración nativa con Węzeł Podpisu (servicio estatal polaco de firma): Profil Zaufany, e-dowód, firma cualificada, mObywatel.",
    "feature_rodo_title": "RGPD por defecto",
    "feature_rodo_body": "Exportación de datos del artículo 20, anonimización del artículo 17, cadena de hashes en la auditoría, alojamiento en Polonia.",
    "feature_i18n_title": "5 idiomas de serie",
    "feature_i18n_body": "Inglés, polaco, ucraniano, alemán y español. Añade más en tiempo de ejecución, sin nueva versión.",
    "feature_api_title": "API primero",
    "feature_api_body": "Todo lo que hay en la interfaz está en la REST API. Especificación OpenAPI publicada automáticamente en api.sign.yt.",
    "pricing_title": "Precios justos, límites transparentes",
    "pricing_sub": "Pago mensual. Sin recargo por firma en los primeros 500 documentos. Cancela cuando quieras.",
    "tier_starter": "Starter",
    "tier_starter_blurb": "Para una oficina que empieza.",
    "tier_starter_b1": "50 documentos / mes",
    "tier_starter_b2": "3 usuarios, 1 equipo",
    "tier_starter_b3": "Correo gestionado, código por correo",
    "tier_starter_cta": "Probar Starter",
    "tier_pro": "Professional",
    "tier_pro_blurb": "Para equipos que quieren su propio dominio.",
    "tier_pro_b1": "500 documentos / mes",
    "tier_pro_b2": "10 usuarios, 5 equipos",
    "tier_pro_b3": "Dominio propio, sin marca de agua",
    "tier_pro_b4": "Código por SMS (50 / mes)",
    "tier_pro_cta": "Empezar con Pro",
    "tier_business": "Business",
    "tier_business_blurb": "Para operadores con varias marcas.",
    "tier_business_b1": "Documentos ilimitados",
    "tier_business_b2": "Usuarios y equipos ilimitados",
    "tier_business_b3": "SSO, webhooks, claves de API, tu propio correo",
    "tier_business_b4": "Soporte prioritario",
    "tier_business_cta": "Hablar con ventas",
    "tier_enterprise": "Enterprise",
    "tier_enterprise_blurb": "Para contratos a medida y on-premise.",
    "tier_enterprise_b1": "Todo lo de Business",
    "tier_enterprise_b2": "SAML SSO, SLA, DPA",
    "tier_enterprise_b3": "Ingeniero de éxito dedicado",
    "tier_enterprise_cta": "Contáctanos",
    "most_popular": "Recomendado",
    "pln_per_month": "PLN / mes",
    "custom": "A medida",
    "trust_eu_hosted": "Alojado en la UE",
    "trust_pades": "Sello PAdES",
    "trust_rodo": "RGPD",
    "trust_audit": "Cadena de hashes de auditoría",
    "who_title": "Hecho para quienes más firman",
    "who_sub": "Tres tipos de equipos en torno a los que construimos sign.yt.",
    "segment_biura_title": "Oficinas virtuales y coworking",
    "segment_biura_body": "Contratos de arrendamiento y de servicios firmados en tu dominio, con tu marca. Cada marca funciona como su propia organización con su propio dominio, y un solo inicio de sesión permite cambiar entre ellas.",
    "segment_kancelarie_title": "Despachos de abogados y asesorías",
    "segment_kancelarie_body": "Hojas de encargo y consentimientos con un PDF sellado y un registro de auditoría que puedes guardar en el expediente del cliente.",
    "segment_hr_title": "Equipos de RR. HH.",
    "segment_hr_body": "Ofertas, NDA y anexos enviados de forma masiva desde plantillas, con seguimiento independiente de cada firmante.",
    "faq_title": "Preguntas frecuentes",
    "faq_q1": "¿Estas firmas son legalmente vinculantes?",
    "faq_a1": "Sí. Según eIDAS, no se puede rechazar una firma electrónica como prueba por el mero hecho de ser electrónica. sign.yt sella cada PDF firmado (PAdES) y conserva un registro de auditoría de quién firmó, cuándo y cómo se verificó. Para los documentos que requieren una forma más fuerte, los firmantes pueden usar Profil Zaufany (perfil oficial polaco de identidad digital) donde esté activado.",
    "faq_q2": "¿Puedo usar mi propio dominio y remitente de correo?",
    "faq_a2": "Sí. Desde el plan Professional puedes añadir un dominio de firma como sign.tuempresa.es con certificado TLS automático. Desde el plan Business el correo también puede salir por tu propio SMTP, Microsoft 365, Resend, Amazon SES o Mailgun, para que las invitaciones lleguen desde tu propia dirección.",
    "faq_q3": "¿Cómo confirman los firmantes su identidad?",
    "faq_a3": "Lo eliges en cada documento: sin paso adicional, un código de un solo uso por correo o un código de un solo uso por SMS. El método usado queda registrado en la auditoría.",
    "faq_q4": "¿Hay una API?",
    "faq_a4": "Sí. Una REST API con claves de API con permisos te permite crear y enviar documentos desde tu propio sistema, y los webhooks te avisan cuando un documento se ve, se firma o se completa.",
    "final_cta_title": "Tus clientes firman. El mérito es tuyo.",
    "final_cta_sub": "Reserva una demo breve y prepararemos una organización de prueba con tu marca.",
    "footer_tag": "Firma electrónica en tu propio dominio.",
    "footer_product": "Producto",
    "footer_developers": "Desarrolladores",
    "footer_legal": "Legal",
    "footer_privacy": "Privacidad",
    "footer_terms": "Condiciones",
    "footer_made_in_pl": "Hecho en Polonia",
    "footer_made_with": "Hecho con",
    "footer_in_poland_by": "en Polonia por",
    "hero_kicker": "Firma electrónica para oficinas, despachos y equipos de RR. HH.",
    "hero_note": "Precios desde 49 PLN / mes netos + IVA. Alojado en la UE.",
    "cta_dashboard": "Ir al panel",
    "how_kicker": "Cómo funciona",
    "how_title": "Del PDF al contrato sellado en cinco pasos",
    "step1_title": "Sube el PDF",
    "step1_body": "Arrastra un contrato o parte de una plantilla guardada. Los documentos inteligentes pueden rellenar los datos de la empresa desde GUS, VIES y la lista blanca de IVA a partir del número fiscal.",
    "step2_title": "Coloca los campos",
    "step2_body": "Arrastra campos de firma, fecha, texto y casillas a la página y asígnalos a cada destinatario. Elige firma en paralelo o secuencial.",
    "step3_title": "Envía desde tu dominio",
    "step3_body": "Las invitaciones salen desde tu propio remitente de correo, y el enlace de firma se abre en sign.tuempresa.es con tu marca.",
    "step4_title": "El firmante se verifica",
    "step4_body": "Antes de firmar, los destinatarios confirman su identidad con un código de un solo uso enviado por correo o SMS. Donde esté activado, también pueden firmar con Profil Zaufany.",
    "step5_title": "PDF sellado y registro de auditoría",
    "step5_body": "El PDF final lleva un sello PAdES que cualquiera puede comprobar, además de un registro de auditoría en el que cada evento está encadenado al anterior mediante un hash.",
    "features_kicker": "Funciones",
    "features_all": "Las {n} funciones",
    "bento_domains_title": "Dominios propios",
    "bento_domains_body": "Los enlaces de firma se abren en sign.tuempresa.es. Un registro CNAME y el TLS se emite automáticamente.",
    "bento_pades_title": "Sello PAdES",
    "bento_pades_body": "Un sello criptográfico en cada PDF, más un registro de auditoría encadenado por hash.",
    "bento_verify_title": "Sabe quién firmó",
    "bento_verify_body": "Códigos por correo o SMS, además de Profil Zaufany (perfil oficial polaco de identidad digital), e-dowód (DNI electrónico polaco) o mObywatel (app oficial polaca de identidad móvil) mediante Węzeł Podpisu (servicio estatal polaco de firma) donde esté activado.",
    "bento_smart_title": "Documentos inteligentes",
    "bento_smart_body": "Introduce un número fiscal y los datos de la empresa se rellenan desde GUS, VIES y la lista blanca de IVA.",
    "bento_api_title": "REST API y webhooks",
    "bento_api_body": "Crea y envía documentos desde tu propio sistema y recibe un aviso cuando se firmen.",
    "who_kicker": "Para quién es",
    "plans_kicker": "Precios",
    "plans_compare": "Compara los planes",
    "plans_per_month": "PLN / mes netos + IVA",
    "security_kicker": "Seguridad y cumplimiento",
    "security_title": "Preparado para superar una auditoría",
    "trust_eu_hosted_body": "Los datos y los documentos se quedan en servidores de la Unión Europea.",
    "trust_pades_body": "Los PDF firmados pueden comprobarse en cualquier lector estándar, o mediante el código QR y el enlace corto de cada documento.",
    "trust_rodo_body": "Las solicitudes de exportación y supresión de datos están integradas, no se gestionan por correo.",
    "trust_audit_body": "Un registro a prueba de manipulación de cada visualización, verificación y firma.",
    "faq_kicker": "FAQ",
    "faq_q5": "¿Qué idiomas se admiten?",
    "faq_a5": "La aplicación y las páginas de firma están disponibles en inglés, polaco, ucraniano, alemán y español.",
    "faq_q6": "¿Dónde se guardan mis datos?",
    "faq_a6": "En servidores de la Unión Europea. Puedes exportar o suprimir datos personales a petición, como exige el RGPD.",
    "contact_kicker": "Contacto",
    "bento_brand_title": "Tu marca, no la nuestra",
    "bento_brand_body": "Logotipo, colores, cuatro temas de página y plantillas de correo. Los equipos pueden usar su propia imagen en las páginas de firma, de verificación y en los correos.",
    "cta_show_demo": "Ver demo",
    "demo_note": "Sin registro. Un entorno privado con contratos de ejemplo que se elimina automáticamente."
  }
}

export default pack
